import { supabase } from '@/lib/supabase';
import type { MoodboardEntry, MoodboardHeader, RequiredCategoryRow } from '@/types';

const STORAGE_BUCKET = 'images';

interface MoodboardJoinRow {
  id: string;
  piece_id: string;
  position: number;
  furniture_pieces: { category: string; name: string; storage_path: string } | null;
}

export async function listMoodboardHeaders(
  styleId: string,
  roomType: string,
): Promise<MoodboardHeader[]> {
  const { data, error } = await supabase
    .from('moodboards')
    .select('id, name, is_active')
    .eq('style_id', styleId)
    .eq('room_type', roomType)
    .order('created_at');
  if (error) throw error;
  return ((data as Array<{ id: string; name: string; is_active: boolean }>) || [])
    .map((r) => ({ id: r.id, name: r.name, isActive: r.is_active }));
}

export async function createMoodboard(
  styleId: string,
  roomType: string,
  name: string,
): Promise<string> {
  const { data, error } = await supabase
    .from('moodboards')
    .insert({ style_id: styleId, room_type: roomType, name })
    .select('id')
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function renameMoodboard(id: string, name: string): Promise<void> {
  const { error } = await supabase.from('moodboards').update({ name }).eq('id', id);
  if (error) throw error;
}

export async function deleteMoodboard(id: string): Promise<void> {
  const { error } = await supabase.from('moodboards').delete().eq('id', id);
  if (error) throw error;
}

export async function setMoodboardActive(id: string, isActive: boolean): Promise<void> {
  const { error } = await supabase.from('moodboards').update({ is_active: isActive }).eq('id', id);
  if (error) throw error;
}

export async function listMoodboard(moodboardId: string): Promise<MoodboardEntry[]> {
  const { data, error } = await supabase
    .from('moodboard_items')
    .select('id, piece_id, position, furniture_pieces(category, name, storage_path)')
    .eq('moodboard_id', moodboardId)
    .order('position');
  if (error) throw error;
  return ((data as unknown as MoodboardJoinRow[]) || []).map((row) => ({
    itemId: row.id,
    pieceId: row.piece_id,
    category: row.furniture_pieces?.category ?? '',
    name: row.furniture_pieces?.name ?? '',
    url: supabase.storage
      .from(STORAGE_BUCKET)
      .getPublicUrl(row.furniture_pieces?.storage_path ?? '').data.publicUrl,
    position: row.position,
  }));
}

export async function addToMoodboard(moodboardId: string, pieceId: string): Promise<void> {
  const { count } = await supabase
    .from('moodboard_items')
    .select('*', { count: 'exact', head: true })
    .eq('moodboard_id', moodboardId);
  const { error } = await supabase
    .from('moodboard_items')
    .insert({ moodboard_id: moodboardId, piece_id: pieceId, position: count ?? 0 });
  if (error) throw error;
}

export async function removeFromMoodboard(itemId: string): Promise<void> {
  const { error } = await supabase.from('moodboard_items').delete().eq('id', itemId);
  if (error) throw error;
}

export async function reorderMoodboard(orderedItemIds: string[]): Promise<void> {
  const results = await Promise.all(
    orderedItemIds.map((id, i) =>
      supabase.from('moodboard_items').update({ position: i }).eq('id', id),
    ),
  );
  const failed = results.find((r) => r.error);
  if (failed?.error) throw failed.error;
}

export async function listRequiredCategories(): Promise<RequiredCategoryRow[]> {
  const { data, error } = await supabase
    .from('moodboard_required_categories')
    .select('room_type, category');
  if (error) throw error;
  return (data as RequiredCategoryRow[]) || [];
}

/** Item categories per moodboard for the given board ids (one query). */
export async function listBoardItemCategories(boardIds: string[]): Promise<Record<string, string[]>> {
  if (boardIds.length === 0) return {};
  const { data, error } = await supabase
    .from('moodboard_items')
    .select('moodboard_id, furniture_pieces(category, is_active)')
    .in('moodboard_id', boardIds);
  if (error) throw error;
  const out: Record<string, string[]> = {};
  for (const row of (data as unknown as Array<{ moodboard_id: string; furniture_pieces: { category: string; is_active: boolean } | null }>) || []) {
    const fp = row.furniture_pieces;
    if (!fp || !fp.is_active) continue;
    (out[row.moodboard_id] ??= []).push(fp.category);
  }
  return out;
}

export async function setRequiredCategories(roomType: string, categories: string[]): Promise<void> {
  const del = await supabase.from('moodboard_required_categories').delete().eq('room_type', roomType);
  if (del.error) throw del.error;
  if (categories.length === 0) return;
  const ins = await supabase
    .from('moodboard_required_categories')
    .insert(categories.map((category) => ({ room_type: roomType, category })));
  if (ins.error) throw ins.error;
}
